import { useIntl, FormattedMessage } from 'react-intl';
import { useBrand } from '@kiwicom/nitro/lib/services/brand/context';
import { Illustration, Box, Heading, Text, Button, ButtonLink } from '@kiwicom/orbit-components';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { events, useLogOnMount } from '@kiwicom/account-tracking';

import usePublishRefereeErrorScreenDisplayed from '../../../services/events/business/refer-friend/usePublishRefereeErrorScreenDisplayed';
import type { ErrorReason } from '../../../services/events/business/refer-friend/usePublishRefereeErrorScreenDisplayed';
import ReferFriendErrorContent from './ReferFriendErrorContent';

type ReferFriendTokenErrorProps = {
  referFriendTokenValidationErrorType: string;
  refereeVoucherAmount?: string;
  maximumRefererCreditTotal?: string;
};

const ReferFriendTokenErrorScreen = ({
  referFriendTokenValidationErrorType,
  refereeVoucherAmount,
  maximumRefererCreditTotal,
}: ReferFriendTokenErrorProps) => {
  const intl = useIntl();
  const { locale } = intl;
  const { company_name: companyName } = useBrand();
  const { query, push } = useRouter();
  const href = `/${query.language as string}/user?tab=refer-friend`;
  const publishRefereeErrorScreenDisplayed = usePublishRefereeErrorScreenDisplayed();

  const errorReasonMap: Record<string, ErrorReason> = {
    ExpiredTokenError: 'expired_token_error',
    ReferralTokenOwnerError: 'link_used_for_own_account',
    ReferFriendNotEligibleError: 'not_eligible_error',
    NotSupportedInAustraliaError: 'not_supported_in_australia',
  };

  useLogOnMount(events.referFriend.SHOWED_ERROR_SCREEN, {
    errorType: referFriendTokenValidationErrorType,
  });

  publishRefereeErrorScreenDisplayed(errorReasonMap[referFriendTokenValidationErrorType], 'v2');

  const handleGoToReferFriend = async () => {
    await push(href);
  };

  const handleGoToSearch = async () => {
    await push(`/${locale}`);
  };

  return (
    <Box
      display="flex"
      direction="column"
      maxWidth="1024px"
      className="mx-auto px-800 h-lvh justify-between lm:justify-center items-center"
    >
      <Box display="flex" align="center" className="flex-col-reverse lm:flex-row flex-[2]">
        <Box display="flex" direction="column" className="flex-[2]" maxWidth="415px">
          {referFriendTokenValidationErrorType === 'ExpiredTokenError' && (
            <>
              <Heading spaceAfter="medium" type="title2">
                <FormattedMessage
                  id="account_v2.refer_friend.error.expired_link.title"
                  defaultMessage="This invitation link has expired, but don't worry!"
                />
              </Heading>
              <Text size="large" spaceAfter="large">
                <FormattedMessage
                  id="account_v2.refer_friend.error.expired_link.description"
                  defaultMessage="Ask your friend to create a new link and send it to you. After you create your account, you'll get a {inviteeVoucherAmount} promo code for your next trip."
                  values={{ inviteeVoucherAmount: refereeVoucherAmount }}
                />
              </Text>

              <Box
                display="flex"
                align="center"
                textAlign="center"
                justify="center"
                className="flex-1"
                tablet={{ textAlign: 'start', justify: 'start' }}
              >
                <Link passHref as={`/${locale}`} href="/[language]" legacyBehavior>
                  <ButtonLink compact href={`/${locale}`} dataTest="ExploreButton">
                    <FormattedMessage
                      id="account_v2.component.explore_button"
                      defaultMessage="Explore {companyName}"
                      values={{ companyName }}
                    />
                  </ButtonLink>
                </Link>
              </Box>
            </>
          )}

          {referFriendTokenValidationErrorType === 'ReferFriendNotEligibleError' && (
            <ReferFriendErrorContent
              title={intl.formatMessage({
                id: 'account_v2.refer_friend.error.not_eligible.title',
                defaultMessage: "Sorry, you can't get this promo code",
              })}
              description={intl.formatMessage(
                {
                  id: 'account_v2.refer_friend.error.not_eligible.description',
                  defaultMessage:
                    "Since you already have a Kiwi.com account, you can't get this promo code, but you can invite your friends to sign up and book a trip with us and we'll give you up to {maximumInviterCreditTotal} in Credit.",
                },
                { maximumInviterCreditTotal: maximumRefererCreditTotal },
              )}
              locale={locale}
              onInviteFriendsClick={handleGoToReferFriend}
            />
          )}

          {referFriendTokenValidationErrorType === 'ReferralTokenAccountHasBookingsError' && (
            <ReferFriendErrorContent
              title={intl.formatMessage({
                id: 'account_v2.refer_friend.error.not_eligible.title',
                defaultMessage: "Sorry, you can't get this promo code",
              })}
              description={intl.formatMessage(
                {
                  id: 'account_v2.refer_friend.error.not_eligible.description',
                  defaultMessage:
                    "Since you already have a Kiwi.com account, you can't get this promo code, but you can invite your friends to sign up and book a trip with us and we'll give you up to {maximumInviterCreditTotal} in Credit.",
                },
                { maximumInviterCreditTotal: maximumRefererCreditTotal },
              )}
              locale={locale}
              onInviteFriendsClick={handleGoToReferFriend}
            />
          )}

          {referFriendTokenValidationErrorType === 'ReferralTokenAccountAgeError' && (
            <ReferFriendErrorContent
              title={intl.formatMessage({
                id: 'account_v2.refer_friend.error.not_eligible.title',
                defaultMessage: "Sorry, you can't get this promo code",
              })}
              description={intl.formatMessage(
                {
                  id: 'account_v2.refer_friend.error.not_eligible.description',
                  defaultMessage:
                    "Since you already have a Kiwi.com account, you can't get this promo code, but you can invite your friends to sign up and book a trip with us and we'll give you up to {maximumInviterCreditTotal} in Credit.",
                },
                { maximumInviterCreditTotal: maximumRefererCreditTotal },
              )}
              locale={locale}
              onInviteFriendsClick={handleGoToReferFriend}
            />
          )}

          {referFriendTokenValidationErrorType === 'ReferralTokenOwnerError' && (
            <ReferFriendErrorContent
              title={intl.formatMessage({
                id: 'account_v2.refer_friend.error.owner_link.title',
                defaultMessage: "It seems that you've opened your own invitation link",
              })}
              description={intl.formatMessage(
                {
                  id: 'account_v2.refer_friend.error.owner_link.description',
                  defaultMessage:
                    'Share it with your friends instead so they can create an account and help you earn up to {maximumInviterCreditTotal} in Credit.',
                },
                { maximumInviterCreditTotal: maximumRefererCreditTotal },
              )}
              locale={locale}
              onInviteFriendsClick={handleGoToReferFriend}
            />
          )}

          {referFriendTokenValidationErrorType === 'LinkDoesNotExistError' && (
            <>
              <Heading spaceAfter="medium" type="title2">
                <FormattedMessage
                  id="account_v2.refer_friend.error.link_does_not_exist.title"
                  defaultMessage="It seems that this link doesn't exist any more"
                />
              </Heading>
              <Text size="large" spaceAfter="large">
                <FormattedMessage
                  id="account_v2.refer_friend.error.link_does_not_exist.description"
                  defaultMessage="Sorry about that! It could be that the link expired or doesn't exist any more."
                />
              </Text>
              <Box
                display="flex"
                direction="column"
                align="center"
                justify="center"
                tablet={{ direction: 'row', justify: 'start' }}
                className="flex-1"
              >
                <Box className="w-full tb:w-auto mb-400 tb:mb-0">
                  <Button onClick={handleGoToSearch} fullWidth>
                    <FormattedMessage
                      id="account_v2.component.go_to_search_button"
                      defaultMessage="Go to search"
                    />
                  </Button>
                </Box>
              </Box>
            </>
          )}
        </Box>

        <Box display="flex" className="flex-[2] mb-0" justify="center">
          <Illustration name="Error404" size="display" />
        </Box>
      </Box>
    </Box>
  );
};

export default ReferFriendTokenErrorScreen;
